import axios from 'axios';
import { appApiEndPoints, cognitoApiEndPoints } from '../constants/ApiEndPoints';
import { getValidIdToken, getValidAccessToken } from './tokenRefreshService';

export const handleError = (err: any):any => {
  if (err.response) {
    return err.response;
  }
  if (err.request) {
    return err.request;
  }
  return err;
};

/**
 * Determine if a URL is a Cognito endpoint (but not the token endpoint).
 * These need the accessToken instead of the idToken.
 */
function isCognitoEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  const cognitoBase = import.meta.env.VITE_COGNITO_URL as string | undefined;
  return !!cognitoBase && url.startsWith(cognitoBase);
}

// Request interceptor: attach the correct token to outgoing requests
axios.interceptors.request.use(async (config) => {
  // Skip token attachment for Cognito token endpoint (uses code/refresh_token grant)
  if (config.url === cognitoApiEndPoints.tokenURL) {
    return config;
  }

  try {
    // Cognito endpoints (e.g. /oauth2/userInfo) need the accessToken
    // Backend API endpoints need the idToken
    const token = isCognitoEndpoint(config.url)
      ? await getValidAccessToken()
      : await getValidIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  } catch {
    // If token retrieval fails, let the request proceed without a token.
    // The response interceptor or server will handle the 401.
  }

  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      // If this is already a retry, reject without further attempts
      if (originalRequest?._retry) {
        return Promise.reject(error);
      }

      // Exclude Cognito token endpoint from retry logic
      if (originalRequest?.url === cognitoApiEndPoints.tokenURL) {
        return Promise.reject(error);
      }

      // Mark as retry, refresh token, and replay the request
      originalRequest._retry = true;
      try {
        const newToken = isCognitoEndpoint(originalRequest.url)
          ? await getValidAccessToken()
          : await getValidIdToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    // Non-401 errors should reject so callers' .catch() handlers fire
    return Promise.reject(error);
  },
);

// UPDATED WHITELIST - Now dynamic with base URL validation
const whitelist = {
  // Static endpoints from your constants
  staticEndpoints: [
    ...Object.values(appApiEndPoints),
    ...Object.values(cognitoApiEndPoints),
  ],
  
  // Allowed base URLs - any endpoint under these will be allowed
  allowedBaseUrls: [
    import.meta.env.VITE_BACKEND,
    import.meta.env.VITE_COGNITO_URL,
  ].filter(Boolean),
  
  // Check if URL is allowed
  includes(url: string): boolean {
    // First check static endpoints
    if (this.staticEndpoints.includes(url)) {
      return true;
    }
    
    // Then check if URL starts with any allowed base URL
    const isFromAllowedBase = this.allowedBaseUrls.some(
      baseUrl => baseUrl && url.startsWith(baseUrl), 
    );
    
    if (isFromAllowedBase) {
      // Additional security checks to prevent SSRF
      const dangerousPatterns = [
        /\.\./,           
        /%2e%2e/i,        
        /localhost/i,     
        /127\.0\.0\.1/,   
        /192\.168\./,     
        /10\./,           
        /172\.(1[6-9]|2[0-9]|3[0-1])\./, 
      ];
      
      // Return false if any dangerous pattern is found
      return !dangerousPatterns.some(pattern => pattern.test(url));
    }
    
    return false;
  },
};

// HTTP Methods - NO CHANGES, using whitelist.includes()
export default {
  getData: (url: string, params: any = null, headers = {}) => {
    if (whitelist.includes(url)) {
    return axios({
    method: 'GET',
    url,
    params,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  }).then((response) => response); 
} 
  return Promise.reject(new Error(`URL not allowed:${url}`));
},
  postData: (url: string, params: any = null, data: any = {}, headers = {}) => { 
    if (whitelist.includes(url)) {
    return axios({
    method: 'POST',
    url,
    data,
    params,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  }).then((response) => response); 
}
  return Promise.reject(new Error(`URL not allowed:${url}`));
},
  putData: (url: string, params: any = null, data: any = {}, headers = {},
    signal: AbortSignal | undefined) => {
    if (whitelist.includes(url)) {
    return axios({ // cancelToken: CancelToken | undefined = undefined
    method: 'PUT',
    url,
    data,
    params,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    signal,
  }).then((response) => response); 
}
  return Promise.reject(new Error(`URL not allowed:${url}`));
},
  deleteData: (url: string, params: any = null, data: any = {}, headers = {}) => { 
    if (whitelist.includes(url)) {
    return axios({
    method: 'DELETE',
    url,
    data,
    params,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  }).then((response) => response);
}
  return Promise.reject(new Error(`URL not allowed:${url}`));
},
};
